import React, { useState, useEffect } from 'react';
import { array, bool, func, object } from 'prop-types';
import styled from 'styled-components';
import ProfCenterModal from 'apps/journey/pages/professional-center/components/prof-center-modal';
import { Input, Dropdown, Textarea } from 'library/components/form-components';
import SelectableSortableList from 'library/components/selectable-sortable-list';
import SelectableGoalRow from 'apps/journey/pages/professional-center/professional-outcomes/components/selectable-goal-row';
import { createOutcomeFromOgsMutation } from 'apps/journey/pages/professional-center/professional-outcomes/queries/createOutcomeFromOgsMutation';
import { NotificationRender } from 'library/components/notification';
import { SubmitButton } from 'apps/journey/pages/professional-center/components/prof-center-modal/styled-prof-center-modal';
import PlusSVG from 'library/icons/plus.svg';
import hasHoc from 'util/hoc/has-hoc';
import 'library/helpers/array';
import { resolve } from 'library/helpers/path-resolver';
import { capitalize } from 'library/helpers/text';
import { withConfig } from 'util/hoc';

const StyledPlusSVG = styled(PlusSVG)`
  color: #7ab3c39e;
`;

const StyledHeading = styled.span`
  display: flex;
  align-items: center;

  svg {
    margin-right: 0.5em;
  }
`;

const StyledBody = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: column;
  box-shadow: 0px 4px 7px 2px rgba(0, 0, 0, 0.2);
  border-top: 5px #37b697 solid;
  border-radius: 0.3em;
  padding: 1em;
  overflow: scroll;
  > * {
    margin-bottom: 1em;
  }
`;

const StyledFormRow = styled.div`
  display: flex;
  width: 100%;
  > :first-child {
    padding-right: 0.5em;
  }
`;

const StyledTagsDropdown = styled(Dropdown)`
  max-width: 270px;
`;

const SubmitCreateOutcomeButton = withConfig(
  ({ handleSubmit, loading, configuration }) => {
    return (
      <SubmitButton disabled={loading} onClick={handleSubmit}>
        Create{' '}
        {capitalize(
          resolve(configuration, 'environment/journey/goals_name', 'Outcome')
        )}
      </SubmitButton>
    );
  }
);

const CreateOutcomesModal = ({
  reloadTable,
  toggleActive,
  active,
  records,
  selectedRecords,
  tagOptions,
  match,
  configuration,
}) => {
  const [formValues, setFormValues] = useState({});
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [goalsAndSteps, setGoalsAndSteps] = useState(
    records.filter((goal) => selectedRecords.includes(goal.id))
  );

  useEffect(() => {
    if (!active) return;
    const selectedGoals = goalsFromSelectedOutcomes();
    setGoalsAndSteps(selectedGoals);
  }, [active]);

  const handleChange = (e) => {
    setFormValues({ ...formValues, [e.target.name]: e.target.value });
  };

  const findGoal = (goalId) => goalsAndSteps.find(({ id }) => id === goalId);

  const handleSelection = (selectionId, type, parentType, parentId) => {
    const goal = type === 'Goal' ? findGoal(selectionId) : findGoal(parentId);

    if (type === 'Goal') {
      if (!goal) return;

      goal.active = !goal.active;

      // Toggling the goal should toggle its steps to match
      goal.steps.map((step) => (step.active = goal.active));
    } else {
      const step = goal.steps.find(({ id }) => id === selectionId);
      step.active = !step.active;

      // If you're checking a step, make sure the goal is also checked
      if (step.active) {
        goal.active = true;
      } else {
        // If no steps are checked, also uncheck the goal
        if (!goal.steps.some((step) => step.active)) {
          goal.active = false;
        }
      }
    }
    setGoalsAndSteps([...goalsAndSteps]);
  };

  const onSortUpdate = (from, to, type, parentType, parentId) => {
    if (type === 'Step') {
      // reorder steps
      const goal = findGoal(parentId);
      const tmp = goal.steps[from];
      goal.steps.splice(from, 1);
      goal.steps.splice(to, 0, tmp);
    } else {
      const tmp = goalsAndSteps[from];
      goalsAndSteps.splice(from, 1);
      goalsAndSteps.splice(to, 0, tmp);
    }
    setGoalsAndSteps([...goalsAndSteps]);
  };

  const handleTagSelection = (e) => {
    setTags([...tags.toggleValues(e.target?.label)]);
  };

  const handleSubmit = async () => {
    setLoading(true);
    const filteredGoals = goalsAndSteps.filter(({ active }) => active);
    const reducedGoals = filteredGoals.map((goal) => ({
      goal_id: goal.id,
      step_ids: goal.steps
        .filter(({ active }) => active)
        .map((step) => step.id),
    }));
    try {
      const result = await createOutcomeFromOgsMutation({
        outcome_name: formValues.outcome_name,
        outcome_description: formValues.outcome_description,
        selections: reducedGoals,
        global: !match?.params?.organizationId,
        tags: tags,
      });
      if (result.data?.create_outcome_from_ogs?.success) {
        NotificationRender(
          'success',
          `New ${resolve(
            configuration,
            'environment/journey/goals_name',
            'outcome'
          )} successfully created!`
        );
        setLoading(false);
        reloadTable();
        toggleActive();
      } else {
        NotificationRender('error', 'Something went wrong, please try again.');
        console.error(result.data?.create_outcome_from_ogs?.errors);
      }
    } catch (e) {
      NotificationRender('error', 'Something went wrong, please try again.');
      console.error(e);
    }
    setLoading(false);
  };

  const isSelected = (node) => {
    node.active;
  };

  const goalsFromSelectedOutcomes = () => {
    const selectedOGsData = records.filter((record) =>
      selectedRecords.includes(record.id)
    );
    const goals = selectedOGsData.reduce((goalsArray, outcome) => {
      if (outcome.goals.length) {
        goalsArray.push(
          ...outcome.goals.map((goal) => {
            goal.active = true;
            goal.steps.map((step) => {
              step.active = true;
              return step;
            });
            return goal;
          })
        );
      }
      return goalsArray;
    }, []);
    return goals;
  };

  return (
    <ProfCenterModal
      active={active}
      loading={loading}
      actionButton={SubmitCreateOutcomeButton}
      handleSubmit={handleSubmit}
      toggleActive={toggleActive}
      modalHeading={
        <StyledHeading>
          <StyledPlusSVG /> Create New{' '}
          {capitalize(
            resolve(configuration, 'environment/journey/goals_name', 'Outcome')
          )}
        </StyledHeading>
      }
      modalDetailText={`Choose the Goals and Steps you want included in the new ${capitalize(
        resolve(configuration, 'environment/journey/goals_name', 'Outcome')
      )}. To reorder Goals or Steps, you can drag and drop them.`}
    >
      <StyledBody>
        <StyledFormRow>
          <Input
            label={`${capitalize(
              resolve(
                configuration,
                'environment/journey/goals_name',
                'Outcome'
              )
            )} Name`}
            name="outcome_name"
            type="text"
            value={formValues?.outcome_name || ''}
            onChange={handleChange}
            key="outcome_name"
          />
          <StyledTagsDropdown
            label="Tags"
            placeholder="Select Tags"
            name="outcome_tags"
            key="outcome_tags"
            onChange={handleTagSelection}
            options={tagOptions}
            multiselect
          />
        </StyledFormRow>
        <Textarea
          label="Description"
          type="textarea"
          name="outcome_description"
          rows="2"
          key="outcome_description"
          value={formValues?.outcome_description || ''}
          onChange={handleChange}
        />
        <SelectableSortableList
          data={goalsAndSteps}
          isSelected={isSelected}
          // rowComponent={SelectableGoalRow}
          onSelect={handleSelection}
          onSortUpdate={onSortUpdate}
          type="Goal"
        >
          {goalsAndSteps.map((goal) => (
            <SelectableGoalRow
              key={goal.id}
              node={goal}
              onSelect={handleSelection}
              onSortUpdate={onSortUpdate}
            />
          ))}
        </SelectableSortableList>
      </StyledBody>
    </ProfCenterModal>
  );
};

CreateOutcomesModal.propTypes = {
  active: bool,
  configuration: object,
  match: object,
  toggleActive: func,
  selectedRecords: array,
  records: array,
  tagOptions: array,
  reloadTable: func,
};

CreateOutcomesModal.defaultProps = {
  tagOptions: [],
};

CreateOutcomesModal.hoc_list = ['withRouter', 'withConfig'];

const areEqual = (prevProps, nextProps) => {
  return prevProps.active === nextProps.active;
};
export default React.memo(hasHoc(CreateOutcomesModal), areEqual);
